import { useState, useMemo, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ScreenshotList } from '@/pages/CADPage/components/ScreenshotPanelButton/ScreenshotList'
import { useScreenshots } from '@/pages/CADPage/components/ScreenshotPanelButton/hooks/useScreenshots'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { ImagesList } from './ImagesList'
import {
  useUpdateTemplateElement,
  TemplateAttribute,
  ImageTemplate,
  ViewTemplate,
} from '@/services/hooks/template_attributes'
import type { Image, View } from '@/lib/api/client'
import { createStepOrderNumber } from '@/pages/CADPage/components/AssemblyOrderPanel/lib/createStepOrderNumber'
import { isEmptyOrSpaces } from '@/utils'

interface ReplaceImageDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  template: TemplateAttribute<ImageTemplate | ViewTemplate>
  documentPageId: string
  documentVersionId: string
  documentId: string
  cadVersionId?: string
  images: Image[]
  isReadOnly: boolean
  onReplaceStart: () => void
  onReplaceEnd: () => void
}

export const ReplaceImageDialog = ({
  isOpen,
  onOpenChange,
  template,
  documentPageId,
  documentVersionId,
  documentId,
  cadVersionId,
  images,
  isReadOnly,
  onReplaceStart,
  onReplaceEnd,
}: ReplaceImageDialogProps) => {
  const { isLoading: isLoadingScreenshots, data: screenshotsData } =
    useScreenshots({
      documentId,
      cadVersionId,
      documentVersionId,
    })
  const { isLoading: isLoadingSteps, steps } = useOperationSteps()
  const isLoadingViewsForReplace = isLoadingScreenshots || isLoadingSteps

  const [filterByOperation, setFilterByOperation] = useState<string | null>(
    null,
  )

  // Set initial filter to current operation when dialog opens
  const initialFilterSet = useRef(false)
  useEffect(() => {
    if (isOpen && !initialFilterSet.current) {
      setFilterByOperation(documentPageId)
      initialFilterSet.current = true
    }
  }, [isOpen, documentPageId])

  // Reset filter when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setFilterByOperation(documentPageId)
      initialFilterSet.current = false
    }
  }, [isOpen, documentPageId])

  const filteredViews = useMemo(() => {
    if (!screenshotsData) return []
    if (!filterByOperation) return screenshotsData

    return screenshotsData.filter(
      (view) => view.document_page === filterByOperation,
    )
  }, [screenshotsData, filterByOperation])

  const { mutateAsync: updateTemplate } = useUpdateTemplateElement({
    documentPageId,
    useOptimisticUpdate: true,
  })

  const handleViewSelect = (view: View, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onOpenChange(false)
    onReplaceStart()

    updateTemplate({
      payload: {
        ...template?.template_values,
        viewId: view.id,
        imageId: undefined,
      },
      data_type: 'view',
      templateAttributeId: template?.id as string,
    })
      .then(() => {
        onReplaceEnd()
      })
      .catch((error) => {
        console.error('Error replacing image:', error)
        onReplaceEnd()
      })
  }

  const handleImageSelect = (image: Image, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onOpenChange(false)
    onReplaceStart()

    updateTemplate({
      payload: {
        ...template?.template_values,
        imageId: image.id,
        viewId: undefined,
      },
      data_type: 'image',
      templateAttributeId: template?.id as string,
    })
      .then(() => {
        onReplaceEnd()
      })
      .catch((error) => {
        console.error('Error replacing image:', error)
        onReplaceEnd()
      })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="min-h-44 max-w-4xl"
        ariaLabel="Select Image Dialog"
        handleClose={() => onOpenChange(false)}
      >
        <DialogHeader>
          <DialogTitle>Select an Image</DialogTitle>
        </DialogHeader>
        <div className="flex max-h-[650px] flex-col overflow-y-scroll">
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-4 pr-1 pt-1">
              <h3 className="text-lg font-bold">CAD Screenshots</h3>
              <Select
                value={filterByOperation || 'all'}
                onValueChange={(value) => {
                  setFilterByOperation(value === 'all' ? null : value)
                }}
              >
                <SelectTrigger className="w-[230px]">
                  <SelectValue placeholder="Filter by Operation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem key="all-steps" value="all">
                      All Operations
                    </SelectItem>
                    {steps.map((step) => (
                      <SelectItem key={step.id} value={String(step.id)}>
                        {isEmptyOrSpaces(step.name)
                          ? `Operation ${createStepOrderNumber({ step, steps })}`
                          : step.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <ScreenshotList
              views={filteredViews}
              steps={steps}
              isLoading={isLoadingViewsForReplace}
              isDisabled={false}
              isReadOnly={isReadOnly}
              onClick={({ view, event }) => handleViewSelect(view, event)}
            />
          </div>
          <div className="flex flex-col">
            <h3 className="text-lg font-bold">Image Gallery</h3>
            <ImagesList
              images={images || []}
              isDisabled={false}
              isLoading={false}
              isReadOnly={isReadOnly}
              onClick={({ image, event }) => handleImageSelect(image, event)}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
